import { Controller } from "@hotwired/stimulus"
import currency from "currency.js"

export default class extends Controller {
  static targets = [
    "clientField", "currency", "discountRate", "taxRate",
    "subtotal", "discount", "tax", "taxLabel", "total",
  ]

  connect() {
    this.recalculate()
  }

  recalculate() {
    const rows = this.activeRows
    const documentTaxRate = this.rateValue(this.taxRateTarget)
    const discountRate = this.rateValue(this.discountRateTarget)
    const buckets = new Map()

    rows.forEach((row) => {
      const amount = this.lineTotal(row)
      const rate = this.lineRate(row, documentTaxRate)
      buckets.set(rate.toString(), { rate, subtotal: (buckets.get(rate.toString())?.subtotal || 0) + amount })

      const totalTarget = row.querySelector("[data-document-editor-line-total]")
      if (totalTarget) totalTarget.textContent = this.format(amount)
      this.updateLineTax(row, documentTaxRate)
    })

    const subtotal = [...buckets.values()].reduce((sum, bucket) => sum + bucket.subtotal, 0)
    let discount = 0
    let tax = 0
    const taxableRates = []
    buckets.forEach((bucket) => {
      const bucketDiscount = this.percentageOf(bucket.subtotal, discountRate)
      discount += bucketDiscount
      tax += this.percentageOf(bucket.subtotal - bucketDiscount, bucket.rate)
      if (bucket.rate > 0n) taxableRates.push(bucket.rate)
    })
    const total = subtotal - discount + tax

    this.subtotalTarget.textContent = this.format(subtotal)
    this.discountTarget.textContent = this.format(-discount)
    this.taxTarget.textContent = this.format(tax)
    this.totalTarget.textContent = this.format(total)
    this.taxLabelTarget.textContent = taxableRates.length === 1
      ? this.taxLabelTarget.dataset.label.replace("%{rate}", this.formatRate(taxableRates[0]))
      : this.taxLabelTarget.dataset.multipleLabel
  }

  editLineTax(event) {
    const row = event.currentTarget.closest("[data-document-editor-line-item]")
    const field = row?.querySelector("[data-document-editor-tax-rate-override]")
    if (!field) return

    this.toggleLineTaxInput(row, true)
    field.focus()
  }

  collapseLineTax(event) {
    const field = event.currentTarget
    if (this.normalizedValue(field.value) !== "") return

    field.value = ""
    this.toggleLineTaxInput(field.closest("[data-document-editor-line-item]"), false)
    this.recalculate()
  }

  clientCreated(event) {
    const autocomplete = this.clientFieldTarget.querySelector("[data-controller~='autocomplete']")
    if (!autocomplete) return

    const input = autocomplete.querySelector("[data-autocomplete-target='input']")
    const hidden = autocomplete.querySelector("[data-autocomplete-target='hidden']")
    input.value = event.detail.name
    hidden.value = event.detail.id
    const controller = this.application.getControllerForElementAndIdentifier(autocomplete, "autocomplete")
    if (controller) controller.selectedText = event.detail.name
    hidden.dispatchEvent(new Event("change", { bubbles: true }))
  }

  get activeRows() {
    return [...this.element.querySelectorAll("[data-document-editor-line-item]")].filter((row) => {
      const destroy = row.querySelector("input[name*='[_destroy]']")
      return !row.hidden && destroy?.value !== "1"
    })
  }

  lineTotal(row) {
    const quantity = this.scaledDecimal(row.querySelector("[data-document-editor-quantity]")?.value, 4)
    const unitPrice = this.moneyValue(row.querySelector("[data-document-editor-unit-price]"))
    return Number(this.roundFraction(quantity * BigInt(unitPrice), 10_000n))
  }

  moneyValue(field) {
    const value = this.normalizedValue(field?.value)
    return value ? currency(value).intValue : 0
  }

  rateValue(field) {
    return this.scaledDecimal(field?.value, 4, { minimum: 0n })
  }

  lineRate(row, documentRate) {
    const field = row.querySelector("[data-document-editor-tax-rate-override]")
    return this.normalizedValue(field?.value) === "" ? documentRate : this.rateValue(field)
  }

  updateLineTax(row, documentRate) {
    const field = row.querySelector("[data-document-editor-tax-rate-override]")
    const defaultTax = row.querySelector("[data-document-editor-default-tax]")
    if (!field || !defaultTax) return

    const rate = this.formatRate(documentRate)
    const label = defaultTax.dataset.label.replace("%{rate}", rate)
    field.placeholder = rate
    defaultTax.textContent = `${rate} %`
    defaultTax.setAttribute("aria-label", label)
    defaultTax.title = label
  }

  toggleLineTaxInput(row, visible) {
    row?.querySelector("[data-document-editor-default-tax]")?.classList.toggle("hidden", visible)
    row?.querySelector("[data-document-editor-tax-input]")?.classList.toggle("hidden", !visible)
  }

  normalizedValue(value) {
    return value?.trim().replace(/[’'\s]/g, "").replace(/,(?=[^,]*$)/, ".").replace(/[.](?=.*[.])/g, "") || ""
  }

  scaledDecimal(value, scale, { minimum } = {}) {
    const normalized = this.normalizedValue(value)
    const match = normalized.match(/^(-?)(\d*)(?:\.(\d*))?$/)
    if (!match || (!match[2] && !match[3])) return 0n

    const factor = 10n ** BigInt(scale)
    const whole = BigInt(match[2] || "0") * factor
    const digits = match[3] || ""
    const fraction = BigInt((digits.slice(0, scale) + "0".repeat(scale)).slice(0, scale) || "0")
    let result = whole + fraction
    if (digits[scale] && digits[scale] >= "5") result += 1n
    if (match[1]) result = -result
    return minimum === undefined || result >= minimum ? result : minimum
  }

  percentageOf(cents, rate) {
    return Number(this.roundFraction(BigInt(cents) * rate, 1_000_000n))
  }

  roundFraction(numerator, denominator) {
    const sign = numerator < 0n ? -1n : 1n
    return sign * ((numerator * sign + denominator / 2n) / denominator)
  }

  format(cents) {
    return currency(cents, { fromCents: true, separator: "'", symbol: "" }).format()
  }

  formatRate(rate) {
    return (Number(rate) / 10_000).toLocaleString(undefined, { maximumFractionDigits: 4 })
  }
}
